import { DashboardTileNode } from "./dashboard-tile-node";
import { TimePeriod } from "./time-period";

export const VALID_GRID_AREAS = ["quarter", "third", "half", "full"] as const;
export type GridArea = (typeof VALID_GRID_AREAS)[number];

export const VALID_FREQUENCIES = [
  "quarterhourly",
  "hourly",
  "daily",
  "weekly",
  "monthly",
  "yearly",
] as const;
export type Frequency = (typeof VALID_FREQUENCIES)[number];

export class Tile {
  private id: string;
  private title: string;
  private position: number;
  private grid_area: GridArea;
  private frequency: Frequency;
  private time_period: TimePeriod;
  private nodes: DashboardTileNode[];
  private use_aggregated_for_single_node: boolean;

  constructor(
    id: string,
    title: string,
    position: number,
    grid_area: GridArea,
    frequency: Frequency,
    time_period: TimePeriod,
    nodes: DashboardTileNode[],
    use_aggregated_for_single_node: boolean,
  ) {
    this.setId(id);
    this.setTitle(title);
    this.setPosition(position);
    this.setGridArea(grid_area);
    this.setFrequency(frequency);
    this.setTimePeriod(time_period);
    this.setNodes(nodes);
    this.setUseAggregatedForSingleNode(use_aggregated_for_single_node);
  }

  removeNodeById(nodeId: string) {
    this.nodes = this.nodes.filter((node) => node.getId() !== nodeId);
  }

  getId(): string {
    return this.id;
  }

  getTitle(): string {
    return this.title;
  }

  getPosition(): number {
    return this.position;
  }

  getGridArea(): GridArea {
    return this.grid_area;
  }

  getTimePeriod(): TimePeriod {
    return this.time_period;
  }

  getFrequency(): Frequency {
    return this.frequency;
  }

  getNodes(): DashboardTileNode[] {
    return this.nodes;
  }
  getUseAggregatedForSingleNode(): boolean {
    return this.use_aggregated_for_single_node;
  }

  setId(id: string) {
    if (!id.trim()) {
      throw new Error("ID cannot be empty or whitespace.");
    }
    this.id = id;
  }

  setTitle(title: string) {
    if (!title.trim()) {
      throw new Error("Title cannot be empty or whitespace.");
    }
    this.title = title;
  }

  setPosition(position: number) {
    if (position < 0 || !position) {
      throw new Error("Position must be a positive number.");
    }
    this.position = position;
  }

  setGridArea(grid_area: GridArea) {
    if (!VALID_GRID_AREAS.includes(grid_area)) {
      throw new Error(
        `Invalid grid_area value. Must be one of: ${VALID_GRID_AREAS.join(
          ", ",
        )}`,
      );
    }
    this.grid_area = grid_area;
  }

  setFrequency(frequency: Frequency) {
    this.frequency = frequency;
  }

  setTimePeriod(time_period: TimePeriod) {
    this.time_period = time_period;
  }

  setNodes(nodes: DashboardTileNode[]) {
    this.nodes = nodes;
  }

  setUseAggregatedForSingleNode(use_aggregated_for_single_node: boolean) {
    this.use_aggregated_for_single_node =
      use_aggregated_for_single_node || false;
  }

  findFirstTextTileIndex() {
    return this.nodes.findIndex((node) => node.getChartType() === "text");
  }

  isItATextTile() {
    return this.findFirstTextTileIndex() !== -1;
  }

  toJSON() {
    return {
      id: this.id,
      title: this.title,
      position: this.position,
      grid_area: this.grid_area,
      frequency: this.frequency,
      time_period: this.time_period.toJSON(),
      nodes: this.nodes.map((node) => node.toJSON()),
      use_aggregated_for_single_node: this.use_aggregated_for_single_node,
    };
  }
}
